import { useState, useEffect, useCallback } from 'react';
import styles from './StatsViewerPage.module.css';

/* ════════════════════════════════════════════════════════════════════════════
 * Private presence-analytics dashboard — reachable ONLY at #/stats.
 * Not linked anywhere in the tool. Reads the per-day history from the Worker's
 * reserved /history?format=json endpoint, authenticated with the ADMIN_TOKEN
 * the user pastes in (kept in the browser only, never in the bundle).
 * ════════════════════════════════════════════════════════════════════════════ */

type MetricRow = { day: string; dimension: string; value: string; count: number };
type PeakRow = { day: string; peak: number };
type SessionRow = { day: string; totalSec: number; sessions: number };
type HistoryData = { generatedAt: string; metric: MetricRow[]; peak: PeakRow[]; session: SessionRow[] };

const TOKEN_KEY = 'aml_stats_token';
const WEEKDAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DIM_LABELS: Record<string, string> = {
  country: 'Countries', city: 'Cities', region: 'Regions', continent: 'Continents',
  provider: 'Network providers', device: 'Devices', browser: 'Browsers', os: 'Operating systems',
};

function sumByValue(metric: MetricRow[], dim: string): { label: string; value: number }[] {
  const m: Record<string, number> = {};
  for (const r of metric) if (r.dimension === dim) m[r.value] = (m[r.value] || 0) + r.count;
  return Object.entries(m).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
}
function byDay(metric: MetricRow[], dim: string): { label: string; value: number }[] {
  const m: Record<string, number> = {};
  for (const r of metric) if (r.dimension === dim) m[r.day] = (m[r.day] || 0) + r.count;
  return Object.entries(m).map(([label, value]) => ({ label, value })).sort((a, b) => a.label.localeCompare(b.label));
}
function fmtDuration(sec: number): string {
  if (!sec || sec < 0) return '–';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

function BarChart({ title, data, accent }: { title: string; data: { label: string; value: number }[]; accent?: string }) {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <div className={styles.bars}>
        {data.map(d => (
          <div key={d.label} className={styles.barRow}>
            <span className={styles.barLabel} title={d.label}>{d.label}</span>
            <div className={styles.barTrack}>
              <div className={styles.barFill} style={{ width: `${(d.value / max) * 100}%`, background: accent || 'var(--accent)' }} />
            </div>
            <span className={styles.barValue}>{d.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function StatsViewerPage() {
  const [token, setToken] = useState<string>(() => {
    try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [input, setInput] = useState('');
  const [data, setData] = useState<HistoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const proxy = import.meta.env.VITE_PROXY_URL as string | undefined;

  const load = useCallback(async (tok: string) => {
    if (!proxy) { setError('Data endpoint not configured (VITE_PROXY_URL missing).'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${proxy.replace(/\/$/, '')}/history?format=json&token=${encodeURIComponent(tok)}`, { cache: 'no-store' });
      if (res.status === 401) { setError('Invalid token.'); setLoading(false); return; }
      if (!res.ok) { setError(`Request failed (${res.status}).`); setLoading(false); return; }
      const json = (await res.json()) as HistoryData;
      setData(json);
      try { localStorage.setItem(TOKEN_KEY, tok); } catch { /* ignore */ }
    } catch {
      setError('Network error. Check the connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [proxy]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const logout = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setToken(''); setData(null); setInput(''); setError('');
  };

  // Download the raw event-level dataset (one row per connection) as CSV.
  const downloadDataset = async () => {
    if (!proxy) return;
    try {
      const res = await fetch(`${proxy.replace(/\/$/, '')}/events?token=${encodeURIComponent(token)}`, { cache: 'no-store' });
      if (!res.ok) { alert(`Download failed (${res.status}).`); return; }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = 'presence_events.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    } catch { alert('Download error.'); }
  };

  // ── Login gate ──
  if (!token || (!data && !loading && error === 'Invalid token.')) {
    return (
      <div className={styles.loginWrap}>
        <div className={styles.loginBox}>
          <h1 className={styles.loginTitle}>Presence Analytics</h1>
          <p className={styles.loginHint}>Enter the access token to view the statistics.</p>
          <input
            className={styles.loginInput}
            type="password"
            value={input}
            placeholder="Access token"
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && input) setToken(input); }}
            autoFocus
          />
          {error && <p className={styles.loginError}>{error}</p>}
          <button className={styles.loginBtn} disabled={!input} onClick={() => setToken(input)}>View statistics</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className={styles.loginWrap}><div className={styles.loginBox}><p className={styles.loginHint}>Loading…</p></div></div>;
  if (error) return <div className={styles.loginWrap}><div className={styles.loginBox}><p className={styles.loginError}>{error}</p><button className={styles.loginBtn} onClick={() => load(token)}>Retry</button><button className={styles.linkBtn} onClick={logout}>Use another token</button></div></div>;
  if (!data) return null;

  const { metric, peak, session } = data;
  const totalConnections = metric.filter(r => r.dimension === 'country').reduce((s, r) => s + r.count, 0);
  const daysTracked = new Set(metric.map(r => r.day)).size;
  const peakMax = peak.reduce((mx, r) => Math.max(mx, r.peak), 0);
  const totSec = session.reduce((s, r) => s + r.totalSec, 0);
  const totSess = session.reduce((s, r) => s + r.sessions, 0);
  const avgSession = totSess ? totSec / totSess : 0;

  const hours = sumByValue(metric, 'hour').sort((a, b) => parseInt(a.label) - parseInt(b.label));
  const weekdays = sumByValue(metric, 'weekday').sort((a, b) => WEEKDAY_ORDER.indexOf(a.label) - WEEKDAY_ORDER.indexOf(b.label));
  const peakByDay = peak.map(r => ({ label: r.day, value: r.peak })).sort((a, b) => a.label.localeCompare(b.label));
  const avgSessByDay = session.map(r => ({ label: r.day, value: r.sessions ? Math.round(r.totalSec / r.sessions) : 0 }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const isEmpty = totalConnections === 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Presence Analytics</h1>
          <p className={styles.sub}>Updated {new Date(data.generatedAt).toLocaleString()} · Europe/Rome days</p>
        </div>
        <div className={styles.actions}>
          <button className={styles.btnGhost} onClick={() => load(token)}>Refresh</button>
          <button className={styles.btn} onClick={downloadDataset}>Download dataset (CSV)</button>
          <button className={styles.btnGhost} onClick={logout}>Log out</button>
        </div>
      </header>

      {isEmpty ? (
        <div className={styles.emptyState}>
          <p>No data yet. The history starts accumulating from the moment the updated Worker is deployed — figures will appear here as visitors connect.</p>
        </div>
      ) : (
        <>
          <div className={styles.kpis}>
            <div className={styles.kpi}><span className={styles.kpiVal}>{totalConnections}</span><span className={styles.kpiLbl}>Total connections</span></div>
            <div className={styles.kpi}><span className={styles.kpiVal}>{daysTracked}</span><span className={styles.kpiLbl}>Days tracked</span></div>
            <div className={styles.kpi}><span className={styles.kpiVal}>{peakMax}</span><span className={styles.kpiLbl}>Peak concurrent</span></div>
            <div className={styles.kpi}><span className={styles.kpiVal}>{fmtDuration(avgSession)}</span><span className={styles.kpiLbl}>Avg. session</span></div>
          </div>

          <div className={styles.grid}>
            <BarChart title="Connections per day" data={byDay(metric, 'country')} />
            <BarChart title="Peak concurrent per day" data={peakByDay} accent="var(--amlar, #7C3AED)" />
            {['country', 'city', 'region', 'continent', 'provider', 'device', 'browser', 'os'].map(dim => (
              <BarChart key={dim} title={DIM_LABELS[dim]} data={sumByValue(metric, dim)} />
            ))}
            <BarChart title="By hour of day" data={hours} accent="var(--amlr, #0D9488)" />
            <BarChart title="By weekday" data={weekdays} accent="var(--amlr, #0D9488)" />
            <BarChart title="Avg. session seconds per day" data={avgSessByDay} accent="var(--tfr, #C2410C)" />
          </div>
        </>
      )}
    </div>
  );
}
